import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  IconButton, 
  Typography 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { spaces, palette, customSizes } from '../../../assets/styles/themes/tokens';
import constants from '../../../constants';
import DraggableSubjectList from '../../onboarding/OnboardingStepWichtigkeit/DraggableSubjectList';
import { Option } from '../../onboarding/OnboardingOptionChipList';
import useScreenSize from '../../../utils/useScreenSize';

export interface OwnProps {
  open: boolean;
  subjects: string[];
  onClose: () => void;
  onConfirm: (value: string[]) => void;
}

const SubjectsDialog: React.FC<OwnProps>  = (props) => {
  const { t } = useTranslation();
  const { subjects, onClose, onConfirm, open } = props;
  const [subjectsOrder, setSubjectsOrder] = useState<Option[]>();
  const { isXsScreen } = useScreenSize();

  useEffect(() => {
    const newSubjectsOrder: Option[] = subjects.flatMap(name => {
      const subject = constants.subjects.find(s => s.text === name);
      return subject
        ? [{
          value: subject.text,
          iconName: subject.iconName,
          name: subject.text,
          isSelected: true,
        }]
        : [];
    });
    setSubjectsOrder(newSubjectsOrder);
  }, [subjects]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    const newSubjectOrder = subjectsOrder?.map(subject => subject.value);
    if (newSubjectOrder) onConfirm(newSubjectOrder);
    onClose();
  };
  
  return (
    <Dialog onClose={handleClose} open={open} fullWidth={true} maxWidth='lg'>
      <DialogTitle
        component='div' 
        sx={{
          paddingTop: spaces.lg,
          paddingBottom: spaces.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant='h5' color={palette.text.primary}>
            {t('Sort your subjects by importance')}
          </Typography>
          <Box>
            <IconButton
              color='default'
              size='small'
              onClick={handleClose}
              data-testid='close-button'
              aria-label={t('Close')}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography 
          variant='subtitle1' 
          color={palette.text.secondary}
        >
          {t('Prioritize your school subjects so we can tailor the content to your needs.')}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: palette.background.paperElevation1,
          padding: `${spaces.md} ${spaces.lg}!important`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingX: spaces.xxxxl,
          }}
        >
          {subjectsOrder && (
            <DraggableSubjectList
              items={subjectsOrder}
              onChange={setSubjectsOrder}
              sxProps={{ width: isXsScreen ? 'auto' : customSizes.subjectsDialog.width.desktop }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: spaces.lg }}
      >
        <Button 
          onClick={handleClose}
          variant='text'
          color='secondary'
          size='medium'
        >
          {t('Cancel')}
        </Button>
        <Button 
          onClick={handleConfirm}
          variant='contained'
          color='primary'
          size='medium'
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubjectsDialog;
